import { useId, useState } from "react";
import type { KeyboardEvent } from "react";
import type { WalletConsumption } from "../api";
import { Badge, Button, Card, Skeleton, Table, cx } from "../components";
import type { TableColumn } from "../components";
import { formatTokens, ratio } from "../format";
import { useBarsGrow } from "../motion";
import { bucketStyle } from "../theme";
import {
  MOIS_FUTURS,
  MOIS_PASSES,
  RYTHMES,
  abregeMois,
  cheminBarre,
  cheminCourbe,
  formatTokensAxe,
  projeter,
} from "./ProjectionConsommation";
import type { Barre, Epuisement, Point, Rythme } from "./ProjectionConsommation";
import { libelleMois } from "./feuilles/mois";
import styles from "./Dashboard.module.css";

/*
 * Géométrie du dessin, en unités du viewBox. Le SVG s'étire à la largeur de la
 * carte ; ces nombres ne sont donc pas des pixels mais des proportions, et
 * l'infobulle se positionne en pourcentage d'eux.
 *
 * Deux panneaux alignés sur les mêmes bandes : la courbe du reste en haut, la
 * plus haute parce que c'est elle qui répond à la question « jusqu'à quand » ;
 * les barres de consommation mensuelle en bas, qui expliquent la pente. Deux
 * échelles, mais jamais sur le même plan : un double axe inventerait une
 * corrélation que la mise en page ne fait que suggérer.
 */
// Calée sur la largeur ordinaire de la carte (≈ 1 000 px) : à 640, le dessin
// était agrandi de 70 % et ses étiquettes dépassaient le texte de la page.
const LARGEUR = 880;
const MARGE = { haut: 22, droite: 16, bas: 36, gauche: 52 };
const HAUTEUR_COURBE = 150;
const ECART_PANNEAUX = 40;
const HAUTEUR_BARRES = 120;
const HAUTEUR = MARGE.haut + HAUTEUR_COURBE + ECART_PANNEAUX + HAUTEUR_BARRES + MARGE.bas;
const LARGEUR_TRACE = LARGEUR - MARGE.gauche - MARGE.droite;
const NB_BANDES = MOIS_PASSES + MOIS_FUTURS;
const BANDE = LARGEUR_TRACE / NB_BANDES;
/* Une barre ne remplit jamais sa bande : le reste est de l'air, et c'est lui
   qui sépare deux barres — jamais un trait dessiné autour. */
const LARGEUR_BARRE = Math.min(32, BANDE - 2);
const RAYON_BARRE = 4;
const RAYON_POINT = 4;
/* En unités du viewBox : le texte suit l'échelle du dessin, comme les barres. */
const TAILLE_TEXTE = 12;

const HAUT_COURBE = MARGE.haut;
const BASE_COURBE = HAUT_COURBE + HAUTEUR_COURBE;
const HAUT_BARRES = BASE_COURBE + ECART_PANNEAUX;
const BASE_BARRES = HAUT_BARRES + HAUTEUR_BARRES;
const FRONTIERE = MARGE.gauche + MOIS_PASSES * BANDE;

function centreBande(i: number): number {
  return MARGE.gauche + i * BANDE + BANDE / 2;
}

/** Ce que dit la synthèse de la fin du carburant, en une phrase. */
function libelleEpuisement(e: Epuisement): string {
  switch (e.type) {
    case "mois":
      return `épuisement estimé en ${libelleMois(e.mois)}`;
    case "au-dela":
      return `épuisement estimé au-delà de ${MOIS_FUTURS} mois`;
    case "deja-epuise":
      return "plus aucun token à consommer";
    case "aucune-consommation":
      return "aucune consommation sur la période, pas de projection possible";
  }
}

/** L'annonce d'une barre et de son point pour qui ne les voit pas. */
function libelleBarre(b: Barre): string {
  const quoi = b.nature === "reel" ? "consommés" : "projetés";
  const encours = b.courant ? ", mois en cours" : "";
  const reste = b.nature === "reel" ? "reste en fin de mois" : "reste estimé";
  return `${libelleMois(b.mois)}${encours} : ${formatTokens(b.montant)} tokens ${quoi}, ${reste} ${formatTokens(b.reste)} tokens.`;
}

/**
 * Silhouette de l'histogramme pendant le premier chargement, aux mêmes
 * proportions que la carte réelle pour que la page ne saute pas.
 */
export function DashboardProjectionSquelette() {
  return <Skeleton variant="block" height="28rem" />;
}

export type DashboardProjectionProps = {
  consommation: WalletConsumption;
};

/**
 * « Consommation et projection » : la courbe du reste de tokens, réelle sur les
 * six derniers mois puis projetée sur six mois au rythme moyen choisi, et
 * au-dessous les barres de consommation mensuelle qui expliquent sa pente.
 *
 * Tout ce qui se calcule est dans `ProjectionConsommation.ts` ; ce composant
 * ne fait que dessiner. Les faits viennent du serveur (mouvements mensuels,
 * reste d'ouverture, mois courant) ; la projection est une hypothèse d'écran,
 * et le sélecteur de rythme la recalcule sans aller-retour.
 *
 * Accessibilité : chaque mois est une cible focalisable qui porte son annonce
 * complète ; l'infobulle s'ouvre au survol comme au focus ; la synthèse texte
 * dit ce que le dessin montre ; et la vue tableau rend chaque valeur sans
 * dessin du tout. La couleur ne porte jamais seule l'identité : réel et
 * projeté ont chacun leur libellé, dans la légende et dans l'infobulle, et le
 * projeté se distingue aussi par son trait pointillé et son fond.
 */
export function DashboardProjection({ consommation }: DashboardProjectionProps) {
  const [rythme, setRythme] = useState<Rythme>(6);
  const [vue, setVue] = useState<"graphique" | "tableau">("graphique");
  const [actif, setActif] = useState<number | null>(null);

  const idTitre = useId();
  const idSynthese = useId();
  const idInfobulle = useId();

  const projection = projeter({ consommation, rythme });
  const barres = [...projection.passees, ...projection.futures];

  // La pousse se rejoue quand la série change — nouveau portefeuille, autre
  // rythme : ce sont les barres projetées qui bougent, et le mouvement le dit.
  const svg = useBarsGrow<SVGSVGElement>({
    dependencies: [consommation, rythme, vue],
  });

  const barreActive = actif === null ? null : barres[actif];
  const epuisement = projection.epuisement;
  const indexEpuisement =
    epuisement.type === "mois"
      ? barres.findIndex(
          (b) => b.mois.annee === epuisement.mois.annee && b.mois.mois === epuisement.mois.mois,
        )
      : -1;

  function hauteurBarre(b: Barre): number {
    return ratio(b.montant, projection.echelleMax) * HAUTEUR_BARRES;
  }

  function yReste(b: Barre): number {
    return BASE_COURBE - ratio(b.reste, projection.echelleReste) * HAUTEUR_COURBE;
  }

  const points: Point[] = barres.map((b, i) => ({ x: centreBande(i), y: yReste(b) }));
  const pointsReels = points.slice(0, MOIS_PASSES);
  // La partie projetée part du dernier point réel : la courbe est continue,
  // seul son trait change.
  const pointsProjetes = points.slice(MOIS_PASSES - 1);
  const dernier = barres[barres.length - 1];

  /** Flèches gauche et droite d'un mois à son voisin, Échap pour sortir. */
  function auClavier(e: KeyboardEvent<SVGSVGElement>) {
    if (actif === null) {
      return;
    }
    const pas = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (pas === 0) {
      if (e.key === "Escape") {
        (e.target as SVGElement).blur();
      }
      return;
    }
    e.preventDefault();
    const suivant = Math.min(NB_BANDES - 1, Math.max(0, actif + pas));
    svg.current?.querySelector<SVGRectElement>(`[data-index="${suivant}"]`)?.focus();
  }

  const synthese = (
    <p className={styles.synthese} id={idSynthese}>
      Rythme moyen sur {rythme} mois écoulés :{" "}
      <strong className="lde-tabular">{formatTokens(projection.moyenne)}</strong> tokens par mois.
      Reste à consommer (disponible et réservé) :{" "}
      <strong className="lde-tabular">{formatTokens(projection.carburant)}</strong> tokens.{" "}
      <strong>{libelleEpuisement(projection.epuisement)}</strong>.
    </p>
  );

  const colonnes: TableColumn<Barre>[] = [
    { key: "mois", header: "Mois", render: (b) => libelleMois(b.mois) },
    {
      key: "nature",
      header: "Nature",
      render: (b) =>
        b.nature === "reel" ? (
          <Badge bucket="consumed" size="sm" dot>
            {b.courant ? "Réel, en cours" : "Réel"}
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm" dot>
            Projeté
          </Badge>
        ),
    },
    {
      key: "montant",
      header: "Consommation",
      numeric: true,
      render: (b) => formatTokens(b.montant),
    },
    { key: "cumul", header: "Cumul", numeric: true, render: (b) => formatTokens(b.cumul) },
    {
      key: "reste",
      header: "Reste en fin de mois",
      numeric: true,
      render: (b) => formatTokens(b.reste),
    },
  ];

  return (
    <Card
      title={<span id={idTitre}>Consommation et projection</span>}
      subtitle="Le reste de tokens sur six mois réels et six mois projetés au rythme moyen choisi, et la consommation mensuelle qui l'explique."
    >
      <div className={styles.projectionCommandes}>
        <div className={styles.rythme} role="group" aria-label="Rythme de projection">
          {RYTHMES.map((r) => (
            <Button
              key={r}
              size="sm"
              variant="ghost"
              aria-pressed={rythme === r}
              onClick={() => setRythme(r)}
            >
              {r} mois
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          aria-pressed={vue === "tableau"}
          onClick={() => setVue(vue === "tableau" ? "graphique" : "tableau")}
        >
          {vue === "tableau" ? "Graphique" : "Tableau"}
        </Button>
      </div>

      {synthese}

      {vue === "tableau" ? (
        <Table
          columns={colonnes}
          rows={barres}
          rowKey={(b) => b.cle}
          caption="Reste de tokens et consommation mensuelle, réels et projetés, en tokens"
          animateRows={false}
        />
      ) : (
        <div className={styles.projection} style={bucketStyle("consumed")}>
          <div className={styles.cadre}>
            <svg
              ref={svg}
              className={styles.graphique}
              viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}
              role="group"
              aria-labelledby={idTitre}
              aria-describedby={idSynthese}
              onKeyDown={auClavier}
            >
              {/* La moitié projetée se distingue par son fond, pas seulement
                  par le trait de sa courbe et la teinte de ses barres. */}
              <rect
                className={styles.zoneFuture}
                x={FRONTIERE}
                y={HAUT_COURBE}
                width={MOIS_FUTURS * BANDE}
                height={BASE_BARRES - HAUT_COURBE}
              />

              {/* ---- Panneau du haut : le reste de tokens ---- */}
              <text
                className={styles.titrePanneau}
                x={MARGE.gauche}
                y={HAUT_COURBE - 9}
                fontSize={TAILLE_TEXTE}
              >
                Reste de tokens en fin de mois
              </text>

              {projection.graduationsReste.map((g) => {
                const y = BASE_COURBE - ratio(g, projection.echelleReste) * HAUTEUR_COURBE;
                return (
                  <g key={`reste-${g}`}>
                    <line
                      className={styles.grille}
                      x1={MARGE.gauche}
                      x2={LARGEUR - MARGE.droite}
                      y1={y}
                      y2={y}
                      shapeRendering="crispEdges"
                    />
                    <text
                      className={styles.axeTexte}
                      x={MARGE.gauche - 8}
                      y={y}
                      textAnchor="end"
                      dominantBaseline="middle"
                      fontSize={TAILLE_TEXTE}
                    >
                      {formatTokensAxe(g)}
                    </text>
                  </g>
                );
              })}

              {/* ---- Panneau du bas : la consommation mensuelle ---- */}
              <text
                className={styles.titrePanneau}
                x={MARGE.gauche}
                y={HAUT_BARRES - 9}
                fontSize={TAILLE_TEXTE}
              >
                Consommation par mois
              </text>

              {projection.graduations.map((g) => {
                const y = BASE_BARRES - ratio(g, projection.echelleMax) * HAUTEUR_BARRES;
                return (
                  <g key={`conso-${g}`}>
                    <line
                      className={styles.grille}
                      x1={MARGE.gauche}
                      x2={LARGEUR - MARGE.droite}
                      y1={y}
                      y2={y}
                      shapeRendering="crispEdges"
                    />
                    <text
                      className={styles.axeTexte}
                      x={MARGE.gauche - 8}
                      y={y}
                      textAnchor="end"
                      dominantBaseline="middle"
                      fontSize={TAILLE_TEXTE}
                    >
                      {formatTokensAxe(g)}
                    </text>
                  </g>
                );
              })}

              {/* La frontière entre ce qui est arrivé et ce qui est supposé,
                  d'un panneau à l'autre. */}
              <line
                className={styles.separateur}
                x1={FRONTIERE}
                x2={FRONTIERE}
                y1={HAUT_COURBE}
                y2={BASE_BARRES}
                shapeRendering="crispEdges"
              />

              {indexEpuisement >= 0 && (
                <g className={styles.epuisement}>
                  <line
                    x1={centreBande(indexEpuisement)}
                    x2={centreBande(indexEpuisement)}
                    y1={HAUT_COURBE}
                    y2={BASE_BARRES}
                  />
                  <text
                    className={styles.axeTexte}
                    x={centreBande(indexEpuisement)}
                    y={HAUT_COURBE - 9}
                    textAnchor={indexEpuisement > NB_BANDES / 2 ? "end" : "start"}
                    dx={indexEpuisement > NB_BANDES / 2 ? -6 : 6}
                    fontSize={TAILLE_TEXTE}
                  >
                    épuisement estimé
                  </text>
                </g>
              )}

              <line
                className={styles.baseline}
                x1={MARGE.gauche}
                x2={LARGEUR - MARGE.droite}
                y1={BASE_COURBE}
                y2={BASE_COURBE}
                shapeRendering="crispEdges"
              />
              <line
                className={styles.baseline}
                x1={MARGE.gauche}
                x2={LARGEUR - MARGE.droite}
                y1={BASE_BARRES}
                y2={BASE_BARRES}
                shapeRendering="crispEdges"
              />

              {/* Les barres, puis la courbe par-dessus : la courbe est le
                  propos, les barres son explication. */}
              {barres.map((b, i) => {
                const h = hauteurBarre(b);
                const x = centreBande(i) - LARGEUR_BARRE / 2;
                const trace = cheminBarre(x, BASE_BARRES - h, LARGEUR_BARRE, h, RAYON_BARRE);
                if (trace === "") {
                  return null;
                }
                return (
                  <path
                    key={b.cle}
                    className={cx(
                      styles.barre,
                      b.nature === "projete" && styles.barreProjetee,
                      actif === i && styles.barreActive,
                    )}
                    d={trace}
                    data-anim="bar"
                  />
                );
              })}

              {/* Un voile sous la partie réelle de la courbe : il rattache la
                  ligne à son axe sans peser. */}
              <path
                className={styles.aireReste}
                data-anim="area"
                d={`${cheminCourbe(pointsReels)} L${pointsReels[pointsReels.length - 1].x},${BASE_COURBE} L${pointsReels[0].x},${BASE_COURBE} Z`}
              />
              <path className={styles.courbe} data-anim="line" d={cheminCourbe(pointsReels)} />
              <path
                className={cx(styles.courbe, styles.courbeProjetee)}
                d={cheminCourbe(pointsProjetes)}
              />

              {points.map((pt, i) => (
                <circle
                  key={barres[i].cle}
                  className={cx(
                    styles.point,
                    barres[i].nature === "projete" && styles.pointProjete,
                    actif === i && styles.pointActif,
                  )}
                  cx={pt.x}
                  cy={pt.y}
                  r={RAYON_POINT}
                />
              ))}

              {/* L'étiquette directe : la valeur au bout de la courbe, la seule
                  que l'on lit sans survoler. */}
              <text
                className={styles.etiquetteFin}
                x={points[points.length - 1].x}
                y={points[points.length - 1].y - 10}
                textAnchor="end"
                fontSize={TAILLE_TEXTE}
              >
                {formatTokensAxe(dernier.reste)}
              </text>

              {/* L'axe des mois, puis les cibles de survol et de focus : toute
                  la hauteur des deux panneaux, pas les seuls pixels peints — un
                  point ou une barre basse resteraient sinon introuvables. */}
              {barres.map((b, i) => (
                <g key={b.cle}>
                  <text
                    className={cx(styles.axeTexte, b.courant && styles.axeCourant)}
                    x={centreBande(i)}
                    y={BASE_BARRES + 18}
                    textAnchor="middle"
                    fontSize={TAILLE_TEXTE}
                  >
                    {abregeMois(b.mois)}
                  </text>
                  {(i === 0 || b.mois.mois === 1) && (
                    <text
                      className={styles.axeTexte}
                      x={centreBande(i)}
                      y={BASE_BARRES + 31}
                      textAnchor="middle"
                      fontSize={TAILLE_TEXTE}
                    >
                      {b.mois.annee}
                    </text>
                  )}
                  <rect
                    className={styles.zoneSurvol}
                    data-index={i}
                    x={MARGE.gauche + i * BANDE}
                    y={HAUT_COURBE}
                    width={BANDE}
                    height={BASE_BARRES - HAUT_COURBE + MARGE.bas}
                    rx={RAYON_BARRE}
                    tabIndex={0}
                    role="img"
                    aria-label={libelleBarre(b)}
                    aria-describedby={actif === i ? idInfobulle : undefined}
                    onPointerEnter={() => setActif(i)}
                    onPointerLeave={() => setActif((a) => (a === i ? null : a))}
                    onFocus={() => setActif(i)}
                    onBlur={() => setActif((a) => (a === i ? null : a))}
                  />
                </g>
              ))}
            </svg>

            {/* Une seule infobulle, déplacée de mois en mois et ancrée au point
                de la courbe. Positionnée en pourcentage du viewBox : elle suit
                le point quelle que soit la largeur rendue. */}
            <div
              role="tooltip"
              id={idInfobulle}
              className={styles.infobulle}
              hidden={actif === null}
              style={
                actif === null || barreActive === null
                  ? undefined
                  : {
                      left: `${(centreBande(actif) / LARGEUR) * 100}%`,
                      top: `${(yReste(barreActive) / HAUTEUR) * 100}%`,
                    }
              }
            >
              {barreActive !== null && (
                <>
                  <div className={styles.infobulleValeur}>
                    {formatTokens(barreActive.reste)} tokens
                    <span className={styles.infobulleNature}>
                      {barreActive.nature === "projete" ? " restants (estimation)" : " restants"}
                    </span>
                  </div>
                  <div className={styles.infobulleLigne}>
                    {libelleMois(barreActive.mois)}
                    {barreActive.courant && " (en cours)"}
                  </div>
                  <div className={styles.infobulleLigne}>
                    {barreActive.nature === "projete" ? "Consommation projetée : " : "Consommé : "}
                    <span className="lde-tabular">{formatTokens(barreActive.montant)}</span>
                  </div>
                  <div className={styles.infobulleLigne}>
                    Cumul : <span className="lde-tabular">{formatTokens(barreActive.cumul)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <ul className={styles.legendeSeries} aria-label="Légende">
            <li className={styles.legendeSerie}>
              <span className={styles.legendeTrait} aria-hidden="true" />
              Reste réel
            </li>
            <li className={styles.legendeSerie}>
              <span
                className={cx(styles.legendeTrait, styles.legendeTraitProjete)}
                aria-hidden="true"
              />
              Reste projeté
            </li>
            <li className={styles.legendeSerie}>
              <span className={styles.legendeSwatch} aria-hidden="true" />
              Consommation réelle
            </li>
            <li className={styles.legendeSerie}>
              <span
                className={cx(styles.legendeSwatch, styles.legendeSwatchProjete)}
                aria-hidden="true"
              />
              Consommation projetée
            </li>
          </ul>
        </div>
      )}
    </Card>
  );
}
